import React, { useState, useEffect } from "react";
import { Container, styled } from "@mui/system";
import { Grid, Typography, TextField, Checkbox, Box, Chip, Select, MenuItem, Button } from "@mui/material";
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import globals from "../globals";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";
import ErrorPage from "../error_page/errorPage";
import LoadingPage from "../error_page/loadingPage";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import LoadingIcon from "../components/loading";
import { areAllArraysEmpty, addDays } from "../utils";

const TextFieldRequest = styled(TextField)(() => ({
    borderRadius: 3,
    backgroundColor: globals.colors.lightPink, // default background
    '&:hover': {
        backgroundColor: globals.colors.lightPink, // no color on hover
    },
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: 'transparent', // no border by default
        },
        '&:hover fieldset': {
            borderColor: 'transparent', // no border on hover
        },
        '&.Mui-focused fieldset': {
            borderColor: globals.colors.lightRed, // red border when typing
        },
    },
}));

const CheckedCheckbox = styled(Checkbox)(() => ({
    color: globals.colors.darkBlue, // default (unchecked) color
    '&.Mui-checked': {
        color: globals.colors.darkBlue, // checked color
    },
    '&:hover': {
        // boxShadow: '0 0 5px lightcoral', // light red shadow on hover
        backgroundColor: globals.colors.lightGray, // optional: remove hover background
    },
}));

const todayDate = new Date();
const startingDay = addDays(todayDate, 3);
let days = [
    "Понеделник", "Вторник", "Сряда", "Четвъртък", "Петък", "Събота", "Неделя"
]
days = days.slice(startingDay.getDay() - 1, 7).concat(days.slice(0, startingDay.getDay() - 1));

const CALENDAR_HEIGHT = 48;
const CALENDAR_PADDIONG_TOP = 8;

const CalendarProps = {
    PaperProps: {
        style: {
            maxHeight: CALENDAR_HEIGHT * 4.5 + CALENDAR_PADDIONG_TOP,
            width: 250,
        },
    },
};

// For Calendar with hours
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};


const RequestMeeting = () => {
    const { id } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [showLoadingIcon, setShowLoadingIcon] = useState(false);
    const [showError, setShowError] = useState(false);

    const [calendar, setCalendar] = React.useState({
        'Понеделник': [],
        'Вторник': [],
        'Сряда': [],
        'Четвъртък': [],
        'Петък': [],
        'Събота': [],
        'Неделя': [],
    });
    const [instructorTimeSlots, setInstructorTimeSlots] = React.useState({});

    // Introductory meeting info state
    const [motivation, setMotivation] = useState('');
    const [goals, setGoals] = useState([]);
    const [teachingPreferences, setTeachingPreferences] = useState([]);
    const [questions, setQuestions] = useState('');

    // Goal options for checkboxes
    const goalOptions = [
        "Да надградя знания и умения",
        "Да придобия основи",
        "Лично удоволствие / Хоби",
        "Да започна работа в сферата",
        "Друго"
    ];

    // Teaching preference options for checkboxes
    const teachingPreferenceOptions = [
        "Теория с примери",
        "Практически задачи",
        "Комбинирано",
        "Не съм сигурен/а (очаквам съвет)"
    ];

    // Handler for goal checkboxes
    const handleGoalChange = (option) => (event) => {
        if (event.target.checked) {
            setGoals(prev => [...prev, option]);
        } else {
            setGoals(prev => prev.filter(g => g !== option));
        }
    };

    // Handler for teaching preference checkboxes
    const handleTeachingPreferenceChange = (option) => (event) => {
        if (event.target.checked) {
            setTeachingPreferences(prev => [...prev, option]);
        } else {
            setTeachingPreferences(prev => prev.filter(p => p !== option));
        }
    };



    useEffect(() => {
        fetch(`${globals.apiUrl}/courses/timeslots/${id}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then(response => response.json())
            .then(data => {
                //console.log(data);
                if (data.detail) {
                    setShowError(true);
                }
                setLoading(false);
                setInstructorTimeSlots({
                    'Понеделник': data.monday,
                    'Вторник': data.tuesday,
                    'Сряда': data.wednesday,
                    'Четвъртък': data.thursday,
                    'Петък': data.friday,
                    'Събота': data.saturday,
                    'Неделя': data.sunday,
                });
            })
            .catch(error => {
                console.error('Error:', error);
                setShowError(true);
            });
    }, [id]);
    // Handler that updates state for a specific day
    const handleCalendarChange = (day) => (event) => {
        // console.log(calendar);
        const { value } = event.target;
        setCalendar((prev) => ({
            ...prev,
            [day]: typeof value === "string" ? value.split(",") : value,
        }));
    };


    const handleSubmit = (e) => {
        e.preventDefault();
        setShowLoadingIcon(true);

        const introductoryTimeSlots = {
            monday: calendar['Понеделник'],
            tuesday: calendar['Вторник'],
            wednesday: calendar['Сряда'],
            thursday: calendar['Четвъртък'],
            friday: calendar['Петък'],
            saturday: calendar['Събота'],
            sunday: calendar['Неделя'],
            motivation: motivation,
            goals: goals,
            teaching_preferences: teachingPreferences,
            questions: questions ? [questions] : [],
        }
        if (areAllArraysEmpty(calendar)) {
            //console.log("All arrays are empty");
            setShowLoadingIcon(false);
            return;
        }
        fetch(`${globals.apiUrl}/courses/timeslots/introductory/${id}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(introductoryTimeSlots),
        })
            .then(response => response.json())
            .then(data => {
                if (data.detail) {
                    //console.log(data.detail);
                }
                //console.log(data);
                setShowLoadingIcon(false);
                navigate('/request-accept');
            })
            .catch(error => {
                console.error('Error:', error);
                // setShowError(true);
            });
    }


    return (
        showError ? <ErrorPage /> : loading ? <LoadingPage /> :
            <Container maxWidth={'sm'}>
                <Grid container direction={'column'} justifyContent={'center'} textAlign={'center'}>
                    {/* text in the beginning */}
                    <Grid item>
                        <Typography sx={{ color: globals.colors.lightRed, fontSize: 30, fontWeight: 'bold' }}>Заявка за опознавателна среща</Typography>
                    </Grid>
                    <Grid item >
                        <Typography sx={{ mt: 2, color: globals.colors.darkBlue, fontSize: 17, fontWeight: 'bold' }}>Срещата е 30 минути, в които ще се запознаем и ще обсъдим нуждите ти. Напълно безплатно.</Typography>
                    </Grid>
                    <Grid item >
                        <Typography sx={{ mt: 2, color: globals.colors.darkBlue, fontSize: 17, fontWeight: 'bold' }}>Отговори на въпросите, за да можем да се подготвим по-добре
                            за срещата и да я направим възможно най-полезна за теб.</Typography>
                    </Grid>
                    <Grid item >
                        <Typography sx={{ mt: 2, color: globals.colors.darkBlue, fontSize: 17, fontWeight: 'bold' }}>Твоите отговори ще помогнат на преподавателя да разбере
                            какви са очакванията ти и какво те вълнува.</Typography>
                    </Grid>

                    {/* 1. Question */}
                    <Grid item>
                        <Typography sx={{ mt: 4, mb: 1, color: globals.colors.lightRed, fontSize: 17, fontWeight: 'bold' }}>1. Какво те мотивира да избереш този курс?</Typography>
                        <TextFieldRequest
                            multiline
                            rows={2}
                            fullWidth
                            maxRows={5}
                            value={motivation}
                            onChange={(e) => setMotivation(e.target.value)}
                        />
                    </Grid>

                    {/* 2. Question */}
                    <Grid item>
                        <Typography sx={{ mt: 4, color: globals.colors.lightRed, fontSize: 17, fontWeight: 'bold' }}>2. Какви са основните ти цели
                            или очаквания от обучението?</Typography>
                        <Grid container justifyContent={'center'} textAlign={'center'}>
                            <Grid item>
                                <FormControl sx={{ mt: 1, mb: 3 }} component="fieldset" variant="standard">
                                    <FormGroup>
                                        {goalOptions.map((option) => (
                                            <FormControlLabel
                                                key={option}
                                                control={
                                                    <CheckedCheckbox
                                                        checked={goals.includes(option)}
                                                        onChange={handleGoalChange(option)}
                                                    />
                                                }
                                                label={option}
                                            />
                                        ))}
                                    </FormGroup>
                                    <FormHelperText>Избери всички, които са верни за теб</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* 3. Question */}
                    <Grid item>
                        <Typography sx={{ mt: 1, color: globals.colors.lightRed, fontSize: 17, fontWeight: 'bold' }}>3. Какъв тип обучение предпочиташ?</Typography>
                        <Grid container textAlign={'center'} justifyContent={'center'}>
                            <Grid item>
                                <FormControl sx={{ mt: 1, ml: 4 }} component="fieldset" variant="standard">
                                    <FormGroup>
                                        {teachingPreferenceOptions.map((option) => (
                                            <FormControlLabel
                                                key={option}
                                                control={
                                                    <CheckedCheckbox
                                                        checked={teachingPreferences.includes(option)}
                                                        onChange={handleTeachingPreferenceChange(option)}
                                                    />
                                                }
                                                label={option}
                                            />
                                        ))}
                                    </FormGroup>
                                    <FormHelperText>Избери всички, които са верни за теб</FormHelperText>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* 4. Question */}
                    <Grid item>
                        <Typography sx={{ mt: 4, mb: 1, color: globals.colors.lightRed, fontSize: 17, fontWeight: 'bold' }}>4. Има ли нещо конкретно, което искаш да обсъдите на срещата?</Typography>
                        <TextFieldRequest
                            multiline
                            rows={2}
                            fullWidth
                            maxRows={5}
                            sx={{ mt: 1 }}
                            value={questions}
                            onChange={(e) => setQuestions(e.target.value)}
                        />
                    </Grid>

                    {/* 5. Choose day and time */}
                    <Grid item>
                        <Typography sx={{ mt: 4, color: globals.colors.lightRed, fontSize: 17, fontWeight: 'bold' }}>5. Посочи поне три възможни дни и часови диапазони, в които си свободен за опознавателната среща:</Typography>
                        <Grid container justifyContent={'center'} textAlign={'center'} spacing={2} sx={{ mt: 1 }}>
                            {days.map((day, i) => {
                                return { day: day, "date": addDays(startingDay, i) }
                            }).filter(({ day }) => instructorTimeSlots[day].length > 0).map(({ day, date }, i) => (
                                <Grid item key={day} sx={{ mb: 3 }}>
                                    <Typography sx={{ color: globals.colors.darkBlue, fontWeight: 'bold' }}>{day} {date.toLocaleDateString("bg-BG", { day: '2-digit', month: '2-digit', year: 'numeric' })}</Typography>
                                    <Grid container sx={{ mt: 1 }}>
                                        <FormControl sx={{ width: 150 }}>
                                            <Select
                                                name={day}
                                                multiple
                                                value={calendar[day] || []}
                                                onChange={handleCalendarChange(day)}
                                                renderValue={(selected) => (
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                        {selected.map((value) => (
                                                            <Chip key={value} label={value} />
                                                        ))}
                                                    </Box>
                                                )}
                                                MenuProps={CalendarProps}
                                                sx={{
                                                    backgroundColor: globals.colors.backgroundColor,
                                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                                        borderColor: globals.colors.darkRed,
                                                    },
                                                }}
                                            >
                                                {instructorTimeSlots[day].map((hour) => (
                                                    <MenuItem key={hour} value={hour}>
                                                        {hour}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    <Grid item>
                        <Grid container justifyContent={'center'} textAlign={'center'}>
                            {showLoadingIcon && <Grid item>
                                <LoadingIcon sx={{ mr: 4, mt: 3 }} />
                            </Grid>}
                            <Grid item>
                                <Button variant='contained' onClick={handleSubmit} type='submit' size='large' sx={{
                                    mt: 3, px: 5, backgroundColor: globals.colors.darkBlue, '&:hover': {
                                        backgroundColor: globals.colors.lightBlue,
                                    }, textTransform: 'none', fontSize: 17
                                }}> Изпрати заявката </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Container >
    );
}

export default RequestMeeting